import { useState, useCallback } from 'react';
import {
  Bookmark,
  Copy,
  Check,
  ExternalLink,
  Info,
  ChevronRight,
  Link as LinkIcon,
  Share,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAddLink } from '@/hooks/useAddLink';
import { getBookmarkletCode } from '@/lib/bookmarklet';

export function Bookmarklet() {
  const [copied, setCopied] = useState(false);
  const [url, setUrl] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const addLink = useAddLink();

  // Simple mobile detection (with ?mobile=true override for testing)
  const searchParams = new URLSearchParams(window.location.search);
  const isMobile =
    searchParams.get('mobile') === 'true' || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleSaveUrl = async () => {
    if (!url.trim()) return;
    try {
      await addLink.mutateAsync({ url: url.trim() });
      setSaveSuccess(true);
      setUrl('');
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      // Error handled by mutation
    }
  };

  // Get bookmarklet code from shared utility
  const bookmarkletCode = getBookmarkletCode();

  // React blocks javascript: URLs as a security precaution.
  // We use a ref callback to set href directly on the DOM element.
  const bookmarkletRef = useCallback(
    (node: HTMLAnchorElement | null) => {
      if (node) node.setAttribute('href', bookmarkletCode);
    },
    [bookmarkletCode]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(bookmarkletCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = bookmarkletCode;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="container max-w-3xl py-8">
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-bold">Bookmarklet</h1>
        <p className="mt-2 text-muted-foreground">
          Save links to Pith from any webpage with one click.
        </p>
      </div>

      {/* Mobile: Install App + Save URL */}
      {isMobile && (
        <>
          {/* Install as App */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Install Pith
              </CardTitle>
              <CardDescription>
                Add Pith to your home screen to save links from the share menu.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ol className="list-inside list-decimal space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="flex-1">
                    Tap the share button <Share className="inline h-4 w-4" /> in your browser
                  </span>
                </li>
                <li>Select "Add to Home Screen"</li>
                <li>Open Pith from your home screen once to complete setup</li>
              </ol>
              <Alert>
                <Share className="h-4 w-4" />
                <AlertDescription>
                  Once installed, you can share any webpage to Pith directly from Safari or Chrome.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Save URL directly */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LinkIcon className="h-5 w-5" />
                Save a Link
              </CardTitle>
              <CardDescription>Or paste a URL to save it directly.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="https://example.com/article"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveUrl()}
                />
                <Button onClick={handleSaveUrl} disabled={addLink.isPending || !url.trim()}>
                  {addLink.isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
              {saveSuccess && (
                <Alert className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700 dark:text-green-300">
                    Link saved! It will appear on your dashboard.
                  </AlertDescription>
                </Alert>
              )}
              {addLink.isError && (
                <Alert variant="destructive">
                  <AlertDescription>Failed to save link. Please try again.</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Desktop: Installation Instructions */}
      {!isMobile && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bookmark className="h-5 w-5" />
              Install the Bookmarklet
            </CardTitle>
            <CardDescription>
              Drag the button below to your bookmarks bar to get started.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center rounded-lg border-2 border-dashed p-6">
              <p className="mb-4 text-sm text-muted-foreground">
                Drag this button to your bookmarks bar:
              </p>
              <a
                ref={bookmarkletRef}
                href="#"
                title="Save to Pith"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90"
                onClick={(e) => e.preventDefault()}
                draggable
              >
                <Bookmark className="h-4 w-4" aria-hidden="true" />
                <span>Save to Pith</span>
              </a>
              <p className="mt-4 text-xs text-muted-foreground">
                (Don't click - drag to your bookmarks bar)
              </p>
            </div>

            {/* Manual installation (collapsed by default) */}
            <details className="group border-t pt-4">
              <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                Install manually
              </summary>
              <div className="mt-4 space-y-6 pl-6">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Bookmarklet Code</span>
                    <Button variant="outline" size="sm" onClick={handleCopy}>
                      {copied ? (
                        <>
                          <Check className="mr-2 h-4 w-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="mr-2 h-4 w-4" />
                          Copy Code
                        </>
                      )}
                    </Button>
                  </div>
                  <pre className="mt-2 max-h-32 overflow-auto rounded-md bg-muted p-3 text-xs">
                    <code className="break-all">{bookmarkletCode}</code>
                  </pre>
                </div>

                <div>
                  <h3 className="mb-3 font-medium">Manual Installation</h3>
                  <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
                    <li>Copy the bookmarklet code above</li>
                    <li>Create a new bookmark in your browser</li>
                    <li>Name it "Save to Pith"</li>
                    <li>Paste the code as the URL</li>
                  </ol>
                </div>
              </div>
            </details>
          </CardContent>
        </Card>
      )}

      {/* How to use (desktop only) */}
      {!isMobile && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              How to Use
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-inside list-decimal space-y-3 text-sm text-muted-foreground">
              <li>Navigate to any webpage you want to save</li>
              <li>Click the bookmarklet in your bookmarks bar</li>
              <li>A popup will appear confirming the link was saved</li>
              <li>The popup will automatically close after 2 seconds</li>
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Tips (desktop only) */}
      {!isMobile && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Tips</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm">
              <li>
                Make sure your bookmarks bar is visible (Ctrl/Cmd + Shift + B in most browsers)
              </li>
              <li>If the popup is blocked, allow popups for this site in your browser settings</li>
              <li>You must be logged in to Pith for the bookmarklet to work</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
